import { Link } from 'react-router-dom'
import {
  FileText,
  Building2,
  DoorOpen,
  ClipboardCheck,
  Plus,
  ArrowRight,
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { useReports, useBuildings, useAllRooms, useOrganizations } from '@/hooks'
import { PageHeader } from '@/components'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import type { Report, Building, Room } from '@/types'

interface StatCardProps {
  title: string
  value: number | string
  description: string
  icon: React.ReactNode
  href?: string
}

function StatCard({ title, value, description, icon, href }: StatCardProps) {
  const content = (
    <Card className={href ? 'transition-colors hover:bg-muted/50' : ''}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="text-muted-foreground">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  )

  if (href) {
    return <Link to={href}>{content}</Link>
  }

  return content
}

const planTierLabels: Record<string, string> = {
  free: 'Free',
  starter: 'Starter',
  professional: 'Professional',
  enterprise: 'Enterprise',
}

const planTierVariants: Record<string, 'default' | 'secondary' | 'outline'> = {
  free: 'outline',
  starter: 'secondary',
  professional: 'default',
  enterprise: 'default',
}

interface RecentReportRowProps {
  report: Report
  buildings?: Building[]
  rooms?: Room[]
}

function RecentReportRow({ report, buildings, rooms }: RecentReportRowProps) {
  const checked = report.checklist.filter((item) => item.checked).length
  const total = report.checklist.length
  const percent = total > 0 ? Math.round((checked / total) * 100) : 0
  const date = new Date(report.created_at)

  const buildingName = buildings?.find((b) => b.id === report.building_id)?.name
  const roomName = rooms?.find((r) => r.id === report.room_id)?.name
  const locationText = [roomName, buildingName].filter(Boolean).join(' — ')

  return (
    <Link
      to={`/reports/${report.id}`}
      className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
          <ClipboardCheck className="h-4 w-4 text-primary" />
        </div>
        <div>
          <p className="text-sm font-medium">
            {locationText || 'Cleaning Report'}
          </p>
          <p className="text-xs text-muted-foreground">
            {date.toLocaleDateString()}{' '}
            {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>
      <Badge variant={percent === 100 ? 'default' : 'secondary'}>
        {checked}/{total} ({percent}%)
      </Badge>
    </Link>
  )
}

export function DashboardPage() {
  const { user } = useAuth()
  const isSuperAdmin = user?.role === 'super_admin'
  const { effectiveOrgId, selectOrg } = useOrg()
  const { data: organizations } = useOrganizations(isSuperAdmin && !effectiveOrgId)

  const { data: reports, isLoading: reportsLoading } = useReports({})
  const { data: buildings, isLoading: buildingsLoading } = useBuildings(effectiveOrgId)
  const { data: rooms, isLoading: roomsLoading } = useAllRooms()

  const isLoading = reportsLoading || buildingsLoading || roomsLoading

  // Calculate stats
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const reportsToday = reports?.filter((r) => new Date(r.created_at) >= today).length ?? 0

  const completedReports =
    reports?.filter((r) => {
      const checked = r.checklist.filter((item) => item.checked).length
      return checked === r.checklist.length && r.checklist.length > 0
    }).length ?? 0

  const recentReports = reports?.slice(0, 5) ?? []

  const canCreate = user?.role === 'super_admin' || user?.role === 'admin'

  if (isSuperAdmin && !effectiveOrgId) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={`Welcome, ${user?.name?.split(' ')[0] || 'User'}`}
          description="Select an organization to view its dashboard"
        />

        {organizations && organizations.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {organizations.map((org) => (
              <Card
                key={org.id}
                className="cursor-pointer transition-colors hover:bg-muted/50"
                onClick={() => selectOrg(org)}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">{org.name}</CardTitle>
                  <CardDescription>{org.contact_email}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Badge variant={planTierVariants[org.plan_tier] ?? 'outline'}>
                    {planTierLabels[org.plan_tier] ?? org.plan_tier}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No organizations found.</p>
            </CardContent>
          </Card>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Welcome, ${user?.name?.split(' ')[0] || 'User'}`}
        description="Here's an overview of your cleaning compliance system"
        actions={
          canCreate && (
            <Button asChild>
              <Link to="/reports/new">
                <Plus className="mr-2 h-4 w-4" />
                New Report
              </Link>
            </Button>
          )
        }
      />

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Reports Today"
          value={isLoading ? '—' : reportsToday}
          description="Cleaning reports submitted today"
          icon={<FileText className="h-4 w-4" />}
          href="/reports"
        />
        <StatCard
          title="Total Reports"
          value={isLoading ? '—' : (reports?.length ?? 0)}
          description={`${completedReports} fully completed`}
          icon={<ClipboardCheck className="h-4 w-4" />}
          href="/reports"
        />
        <StatCard
          title="Buildings"
          value={isLoading ? '—' : (buildings?.length ?? 0)}
          description="Active buildings in your organization"
          icon={<Building2 className="h-4 w-4" />}
          href="/buildings"
        />
        <StatCard
          title="Rooms"
          value={isLoading ? '—' : (rooms?.length ?? 0)}
          description="Total rooms being tracked"
          icon={<DoorOpen className="h-4 w-4" />}
          href="/rooms"
        />
      </div>

      {/* Recent Reports */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Recent Reports</CardTitle>
            <CardDescription>Latest cleaning reports from your team</CardDescription>
          </div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/reports">
              View all
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-muted-foreground text-sm">Loading...</p>
          ) : recentReports.length > 0 ? (
            <div className="space-y-1">
              {recentReports.map((report) => (
                <RecentReportRow key={report.id} report={report} buildings={buildings} rooms={rooms} />
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <ClipboardCheck className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                No reports yet. Create your first cleaning report to get started.
              </p>
              {canCreate && (
                <Button className="mt-4" asChild>
                  <Link to="/reports/new">
                    <Plus className="mr-2 h-4 w-4" />
                    New Report
                  </Link>
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      {(user?.role === 'super_admin' || user?.role === 'admin') && (
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks for administrators</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <Button variant="outline" asChild className="justify-start">
                <Link to="/buildings">
                  <Building2 className="mr-2 h-4 w-4" />
                  Manage Buildings
                </Link>
              </Button>
              <Button variant="outline" asChild className="justify-start">
                <Link to="/rooms">
                  <DoorOpen className="mr-2 h-4 w-4" />
                  Manage Rooms
                </Link>
              </Button>
              <Button variant="outline" asChild className="justify-start">
                <Link to="/assets">
                  <ClipboardCheck className="mr-2 h-4 w-4" />
                  Manage Assets
                </Link>
              </Button>
              <Button variant="outline" asChild className="justify-start">
                <Link to="/users">
                  <FileText className="mr-2 h-4 w-4" />
                  Manage Users
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
